/**
 * Atlas — Property lifecycle UI.
 *
 * Three pieces, exported separately so PropertyDetailPage can drop them
 * in without restructuring its header:
 *
 *   - <PendingDeletionBanner property onReinstate />
 *       Yellow strip at the top of the page while the property sits in
 *       its 24h grace window. Surfaces a Reinstate button.
 *
 *   - <DeletePropertyModal property onClose onDeleted />
 *       Confirmation modal — owner types the address to confirm + an
 *       optional reason. POSTs to /properties/{id}/delete.
 *
 *   - <TransferPropertyModal property onClose onSent />
 *       Recipient email + per-section history checklist + confirm by
 *       typing the address. POSTs to /properties/{id}/transfer.
 *
 *   - <PropertyDangerMenu property onAfterDelete onAfterTransfer />
 *       Kebab button that opens a tiny popover with "Delete property"
 *       and "Transfer ownership" actions. Each opens its modal.
 */
import React, { useEffect, useState } from 'react';
import { propertyAPI } from '../../services/api';

function fmtDeadline(iso) {
  if (!iso) return '';
  const dt = new Date(Date.parse(iso) + 24 * 60 * 60 * 1000);
  return dt.toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

// ── Banner ────────────────────────────────────────────────────────────────
export function PendingDeletionBanner({ property, onReinstate }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!property?.pending_deletion_at) return null;

  const handle = async () => {
    setBusy(true); setError(null);
    try {
      await propertyAPI.reinstate(property.id);
      onReinstate?.();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not reinstate the property.');
    } finally { setBusy(false); }
  };

  return (
    <div style={{
      background: '#fff8e8', border: '1px solid #f0d97a', borderRadius: 10,
      padding: '14px 18px', marginBottom: 16,
      display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
    }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontWeight: 700, color: '#a06000', fontSize: 14 }}>
          Scheduled for deletion · {fmtDeadline(property.pending_deletion_at)}
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 4 }}>
          Anyone with grant access can no longer see this property. Bookings
          are on hold. Reinstate to bring it back; otherwise it's permanently
          removed 24 hours after deletion was scheduled.
        </div>
        {error && <div style={{ color: 'var(--crimson)', fontSize: 12, marginTop: 6 }}>{error}</div>}
      </div>
      <button className="btn primary sm" disabled={busy} onClick={handle}>
        {busy ? '…' : 'Reinstate'}
      </button>
    </div>
  );
}

// ── Danger menu (kebab) ───────────────────────────────────────────────────
export function PropertyDangerMenu({ property, onAfterDelete, onAfterTransfer }) {
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null); // 'delete' | 'transfer'

  // Hide menu entries that don't apply right now.
  const canDelete = !property?.pending_deletion_at;
  const canTransfer = !property?.pending_deletion_at;

  // Close popover on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (!e.target.closest?.('[data-danger-menu]')) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <>
      <div data-danger-menu style={{ position: 'relative' }}>
        <button
          className="btn ghost sm"
          onClick={() => setOpen((v) => !v)}
          aria-label="More actions"
          style={{ minWidth: 32, padding: '0 8px' }}
        >
          ⋯
        </button>
        {open && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 4px)', right: 0,
            background: 'var(--card)', border: '1px solid var(--hairline)',
            borderRadius: 8, padding: 4, minWidth: 200, zIndex: 30,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          }}>
            <MenuItem
              disabled={!canTransfer}
              onClick={() => { setOpen(false); setModal('transfer'); }}
              label="Transfer ownership…"
              hint="Hand this property to another PropAtlas user."
            />
            <MenuItem
              disabled={!canDelete}
              onClick={() => { setOpen(false); setModal('delete'); }}
              label="Delete property…"
              danger
              hint="24-hour grace before permanent removal."
            />
          </div>
        )}
      </div>

      {modal === 'delete' && (
        <DeletePropertyModal
          property={property}
          onClose={() => setModal(null)}
          onDeleted={() => { setModal(null); onAfterDelete?.(); }}
        />
      )}
      {modal === 'transfer' && (
        <TransferPropertyModal
          property={property}
          onClose={() => setModal(null)}
          onSent={() => { setModal(null); onAfterTransfer?.(); }}
        />
      )}
    </>
  );
}

function MenuItem({ label, hint, danger, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'block', width: '100%', textAlign: 'left',
        background: 'none', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
        padding: '8px 10px', borderRadius: 6,
        opacity: disabled ? 0.5 : 1,
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = 'var(--paper-2)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: danger ? 'var(--crimson)' : 'var(--ink)' }}>
        {label}
      </div>
      {hint && (
        <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>{hint}</div>
      )}
    </button>
  );
}

// ── Delete modal ──────────────────────────────────────────────────────────
export function DeletePropertyModal({ property, onClose, onDeleted }) {
  const addressLine = property?.address_line1 || '';
  const [confirm, setConfirm] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const ok = confirm.trim().toLowerCase() === addressLine.trim().toLowerCase();

  const submit = async () => {
    if (!ok) return;
    setBusy(true); setError(null);
    try {
      await propertyAPI.scheduleDelete(property.id, reason.trim() || undefined);
      onDeleted();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not schedule deletion.');
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div style={{ fontWeight: 700, color: 'var(--crimson)' }}>Delete property</div>
        </div>
        <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55 }}>
            This schedules <strong>{addressLine}</strong> for deletion. For the
            next <strong>24 hours</strong> only you can see it. After that the
            property is permanently archived: anyone with grant access loses
            it, bookings are cancelled, share links are revoked.
          </div>
          <div className="field">
            <label>Type the street address to confirm</label>
            <input
              className="input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={addressLine}
              autoFocus
            />
          </div>
          <div className="field">
            <label>Reason (optional)</label>
            <textarea
              className="input" rows={2} maxLength={500}
              value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Sold / consolidated / mistake / etc."
              style={{ height: 'auto', padding: '8px 12px' }}
            />
          </div>
          {error && <div style={{ color: 'var(--crimson)', fontSize: 12 }}>{error}</div>}
        </div>
        <div className="modal-foot" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            className="btn primary"
            style={{ background: 'var(--crimson)', borderColor: 'var(--crimson)' }}
            disabled={!ok || busy}
            onClick={submit}
          >
            {busy ? '…' : 'Schedule deletion'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Transfer modal ────────────────────────────────────────────────────────
export function TransferPropertyModal({ property, onClose, onSent }) {
  const addressLine = property?.address_line1 || '';
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [include, setInclude] = useState({
    include_timeline: true,
    include_financials: true,
    include_systems: true,
    include_documents: true,
    include_photos: true,
    include_inspections: true,
  });
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const ok = email.includes('@') && confirm.trim().toLowerCase() === addressLine.trim().toLowerCase();

  const submit = async () => {
    if (!ok) return;
    setBusy(true); setError(null);
    try {
      await propertyAPI.initiateTransfer(property.id, {
        to_email: email.trim().toLowerCase(),
        ...include,
        note: note.trim() || undefined,
      });
      onSent();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not initiate the transfer.');
    } finally { setBusy(false); }
  };

  const SECTIONS = [
    ['include_timeline',    'Timeline entries'],
    ['include_financials',  'Financial records'],
    ['include_systems',     'Property systems'],
    ['include_documents',   'Documents'],
    ['include_photos',      'Photos'],
    ['include_inspections', 'Inspection grades'],
  ];

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div style={{ fontWeight: 700, color: 'var(--forest)' }}>Transfer ownership</div>
        </div>
        <div className="modal-body" style={{ display: 'grid', gap: 14 }}>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.55 }}>
            Send a transfer request for <strong>{addressLine}</strong>. The
            recipient must accept before ownership changes. All existing
            grant-access users will lose access — the new owner will need
            to grant access again.
          </div>

          <div className="field">
            <label>Recipient's PropAtlas email</label>
            <input
              className="input" type="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="recipient@example.com"
              autoFocus
            />
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 6 }}>
              Include in transfer
            </div>
            <div style={{ display: 'grid', gap: 6, padding: '10px 12px', background: 'var(--paper-2)', borderRadius: 8 }}>
              {SECTIONS.map(([key, label]) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={include[key]}
                    onChange={(e) => setInclude((s) => ({ ...s, [key]: e.target.checked }))}
                  />
                  {label}
                </label>
              ))}
              <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 4 }}>
                Address, bedrooms, bathrooms, land size, and year built always transfer.
              </div>
            </div>
          </div>

          <div className="field">
            <label>Note to recipient (optional)</label>
            <textarea
              className="input" rows={2} maxLength={500}
              value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Anything they need to know"
              style={{ height: 'auto', padding: '8px 12px' }}
            />
          </div>

          <div className="field">
            <label>Type the street address to confirm</label>
            <input
              className="input"
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              placeholder={addressLine}
            />
          </div>

          {error && <div style={{ color: 'var(--crimson)', fontSize: 12 }}>{error}</div>}
        </div>
        <div className="modal-foot" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" disabled={!ok || busy} onClick={submit}>
            {busy ? '…' : 'Send transfer request'}
          </button>
        </div>
      </div>
    </div>
  );
}
